import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ApiError, getErrorMessage } from '@/shared/api/errors';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import {
  useCreateClientMutation,
  isEmailAlreadyExistsError,
} from '../../api/useClientsQueries';
import type { ClientUser } from '../../api/clientsApi';
import {
  AUTHORIZATION_LETTER_MAX_BYTES,
  isPdfFile,
  type AddClientFormValues,
} from './AddClientPage.types';

const CROSS_AGENCY_CODE = 'COMPANY_REGISTERED_WITH_OTHER_AGENCY';

export type CreatedClientResult = {
  client: ClientUser;
  requiresVerification?: boolean;
  message?: string;
};

type UseAddClientFormOptions = {
  /** Called after successful create. If omitted, navigates to /clients. */
  onCreated?: (result: CreatedClientResult) => void;
};

/** Owns the create-client form: field registration, env country mobile, and submission. */
export function useAddClientForm(options: UseAddClientFormOptions = {}) {
  const { onCreated } = options;
  const navigate = useNavigate();
  const mobileConfig = useMemo(() => getMobileConfig(), []);
  const [authorizationLetterMediaId, setAuthorizationLetterMediaId] = useState<string | null>(
    null,
  );
  const [authorizationLetterFileName, setAuthorizationLetterFileName] = useState<string | null>(
    null,
  );
  const [authorizationLetterError, setAuthorizationLetterError] = useState<string | undefined>();
  const [crossAgencyPrompt, setCrossAgencyPrompt] = useState<{
    open: boolean;
    companyName: string;
  }>({ open: false, companyName: '' });
  const pendingPayloadRef = useRef<Record<string, unknown> | null>(null);
  const createMut = useCreateClientMutation();
  const uploadMut = useUploadSingleFile();
  const deleteFile = useDeleteFile();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<AddClientFormValues>({ mode: 'onTouched' });

  const clearAuthorizationLetter = () => {
    const id = authorizationLetterMediaId?.trim();
    if (id) {
      void deleteFile.mutateAsync({ mediaId: id }).catch(() => {});
    }
    setAuthorizationLetterMediaId(null);
    setAuthorizationLetterFileName(null);
    setAuthorizationLetterError(undefined);
  };

  const onSelectAuthorizationLetter = async (file: File) => {
    setAuthorizationLetterError(undefined);
    if (!isPdfFile(file)) {
      setAuthorizationLetterError('Only PDF files are allowed');
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > AUTHORIZATION_LETTER_MAX_BYTES) {
      setAuthorizationLetterError('PDF must be under 10MB');
      toast.error('PDF must be under 10MB');
      return;
    }

    const previousId = authorizationLetterMediaId?.trim();
    if (previousId) {
      void deleteFile.mutateAsync({ mediaId: previousId }).catch(() => {});
    }

    setAuthorizationLetterFileName(file.name);
    setAuthorizationLetterMediaId(null);

    try {
      const response = await uploadMut.mutateAsync({
        file,
        moduleName: 'authorization-letter',
      });
      const mediaId = response?.data?.mediaId as string | undefined;
      if (!mediaId) {
        throw new Error('Upload did not return a media id');
      }
      setAuthorizationLetterMediaId(mediaId);
    } catch (err) {
      clearAuthorizationLetter();
      const message =
        err instanceof Error ? err.message : 'Failed to upload authorization letter';
      setAuthorizationLetterError(message);
      toast.error(message);
    }
  };

  const buildPayload = (
    data: AddClientFormValues,
    opts?: { confirmCrossAgencyCompany?: boolean },
  ) => {
    const nameParts = data.contactPersonName.trim().split(/\s+/);
    const firstName = nameParts[0] || data.clientName.trim();
    const lastName = nameParts.slice(1).join(' ') || '';
    const email = data.email?.trim() || undefined;

    return {
      companyName: data.clientName.trim(),
      firstName,
      lastName,
      ...(email ? { email } : {}),
      mobileNumber: data.mobileNumber.trim(),
      mobileCountryCode: mobileConfig.code,
      businessCategory: data.businessCategory,
      businessSubCategory: data.businessSubCategory,
      address: data.address.trim(),
      pincode: data.pin.trim(),
      pin: data.pin.trim(),
      fullAddress: data.address.trim(),
      ...(authorizationLetterMediaId
        ? { documents: { entity: authorizationLetterMediaId } }
        : {}),
      ...(opts?.confirmCrossAgencyCompany
        ? { confirmCrossAgencyCompany: true }
        : {}),
    };
  };

  const handleCreateSuccess = (data: unknown) => {
    setCrossAgencyPrompt({ open: false, companyName: '' });
    pendingPayloadRef.current = null;
    // clearAuthorizationLetter();
    reset();

    const payload = data as {
      message?: string;
      data?: {
        client?: ClientUser;
        requiresVerification?: boolean;
      };
    };
    const client = payload?.data?.client;
    if (onCreated && client?._id) {
      onCreated({
        client,
        requiresVerification: payload?.data?.requiresVerification,
        message: payload?.message,
      });
      return;
    }
    navigate('/clients');
  };

  const submitPayload = (payload: Record<string, unknown>) => {
    createMut.mutate(payload, {
      onSuccess: handleCreateSuccess,
      onError: (error) => {
        if (error instanceof ApiError && error.code === CROSS_AGENCY_CODE) {
          pendingPayloadRef.current = payload;
          setCrossAgencyPrompt({
            open: true,
            companyName: String(payload.companyName || ''),
          });
          return;
        }
        if (isEmailAlreadyExistsError(error)) {
          setError('email', {
            type: 'server',
            message: getErrorMessage(error, 'User with this email already exists'),
          });
          return;
        }
      },
    });
  };

  const onSubmit = (data: AddClientFormValues) => {
    submitPayload(buildPayload(data));
  };

  const onContinueCrossAgency = () => {
    const base = pendingPayloadRef.current;
    if (!base) {
      setCrossAgencyPrompt({ open: false, companyName: '' });
      return;
    }
    submitPayload({ ...base, confirmCrossAgencyCompany: true });
  };

  const onCancelCrossAgency = () => {
    setCrossAgencyPrompt({ open: false, companyName: '' });
    pendingPayloadRef.current = null;
  };

  return {
    navigate,
    mobileConfig,
    register,
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    errors,
    handleSubmit: handleSubmit(onSubmit),
    isSaving: createMut.isPending,
    authorizationLetterFileName,
    authorizationLetterError,
    isUploadingAuthorizationLetter: uploadMut.isPending,
    onSelectAuthorizationLetter,
    clearAuthorizationLetter,
    crossAgencyPrompt,
    onContinueCrossAgency,
    onCancelCrossAgency,
  };
}
